import { FormEvent, useRef, useState } from 'react'
import classNames from 'classnames'

import type { CommentTopic } from 'services/content/types'
import type { Comment as CommentType } from 'services/comments/types'
import Details from './Details'
import RichText from 'ui-component-library/components/00-Primitives/Inputs/RichText'
import EditButton from '../EditButton'
import ShareButton from '../ShareButton'
import PrintButton from '../PrintButton'
import ReplyButton from '../ReplyButton'
import FormButtons from '../FormButtons'

import style from './style.scss'

type Props = {
  topic?: CommentTopic
  className?: string
  comment: CommentType
  commentedOnText: string
  textLabelText: string
  editButtonText: string
  replyButtonText: string
  shareButtonText: string
  shareTooltipText: string
  printButtonText: string
  editedText: string
  onToggleEditForm?: () => void
  onReply?: () => void
  onShare: () => void
  userId?: string
  children?: React.ReactNode
  replyForm?: React.ReactNode
  isEditing?: boolean
  closeFormLabelText: string
  submitButtonText: string
  onSubmitEdit: (text: string) => Promise<void>
}

function Comment({
  topic,
  comment,
  className,
  commentedOnText,
  editButtonText,
  shareButtonText,
  shareTooltipText,
  replyButtonText,
  printButtonText,
  textLabelText,
  editedText,
  onToggleEditForm,
  onReply,
  onShare,
  children,
  replyForm,
  isEditing,
  closeFormLabelText,
  submitButtonText,
  onSubmitEdit,
}: Props) {
  const { id, author, createdAt, wasEdited } = comment
  const commentRef = useRef<HTMLDivElement | null>(null)
  const [text, setText] = useState(comment.text)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSubmitEdit(text)
  }

  return (
    <div
      className={classNames(style.wrapper, className)}
      id={`comment-${id}`}
      ref={commentRef}
    >
      <Details
        commentedOnText={commentedOnText}
        authorName={author.name}
        createdAt={createdAt}
        topicTitle={topic && topic.title}
      />
      {isEditing && onToggleEditForm ? (
        <form onSubmit={handleSubmit}>
          <RichText label={textLabelText} updateField={setText} value={text} />
          <FormButtons
            submitButtonText={submitButtonText}
            closeButtonLabelText={closeFormLabelText}
            onClose={onToggleEditForm}
          />
        </form>
      ) : (
        <RichText readOnly label={textLabelText} value={comment.text} />
      )}
      {wasEdited && <div className={style.editedText}>({editedText})</div>}
      <div className={style.actionButtons}>
        {onToggleEditForm && (
          <EditButton onClick={onToggleEditForm}>{editButtonText}</EditButton>
        )}

        {onReply && (
          <ReplyButton onClick={onReply}>{replyButtonText}</ReplyButton>
        )}

        <ShareButton onClick={onShare} tooltipText={shareTooltipText}>
          {shareButtonText}
        </ShareButton>

        <PrintButton contentRef={commentRef}>{printButtonText}</PrintButton>
      </div>
      <div className={style.replies}>
        {children}
        {replyForm}
      </div>
    </div>
  )
}
export default Comment
